import { forwardRef } from 'react';
import { styled } from 'goober';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import { FixedPanelWithShadowLayer } from '@/components/fixed-panel-with-shadow-layer/fixed-panel-with-shadow-layer';
import { useSidebars } from '@/hooks/use-sidebars';
import { colors } from '@/theme/colors';
import { Button } from '@/ui/button/button';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { colorIcon } from '@/ui/icons/functions';
import { Icon } from '@/ui/icons/icon';
import { useCurrentUser } from '@/user/use-current-user.query';

import { NotificationsContent } from './components/notifications-content';
import { useHasUnreadNotifications } from './hooks/use-has-unread-notifications';
import { useMarkNotificationAsRead } from './hooks/use-mark-notification-as-read.mutation';
import { useNotifications } from './hooks/use-notifications.query';
import { isPendingRequestsViewOpenState } from './notifications.state';
import { PendingRequestsContent } from './pending-requests/pending-requests-content';

export const NotificationsSidebar = () => {
  const { t } = useTranslation('notification');
  const { isNotificationsSidebarOpen, toggleNotificationsSidebar } =
    useSidebars();
  const { data: user } = useCurrentUser();
  const { data: notifications, isLoading: notificationsIsLoading } =
    useNotifications(user?.id ?? null);
  const hasUnreadNotifications = useHasUnreadNotifications();
  const { markAsRead } = useMarkNotificationAsRead();
  const [isPendingRequestsViewOpen, setPendingRequestView] = useAtom(
    isPendingRequestsViewOpenState,
  );

  const handleClose = () => {
    setPendingRequestView(false);
    toggleNotificationsSidebar();
  };

  if (!isNotificationsSidebarOpen) return null;

  return (
    <SidebarWrapper onOutsideClick={handleClose}>
      <Container name="content-item-sidebar-container" direction="column">
        <Header
          name="content-item-sidebar-header"
          alignItems="center"
          justify="space-between"
        >
          <TitleContainer
            name="content-item-sidebar-title"
            alignItems="center"
            gap="24px"
            onClick={
              isPendingRequestsViewOpen
                ? () => setPendingRequestView(false)
                : toggleNotificationsSidebar
            }
          >
            <Icon icon="ArrowRight" />
            <TitleValue>
              {isPendingRequestsViewOpen
                ? t`pendingRequests`
                : t`notifications`}
            </TitleValue>
          </TitleContainer>
          {!isPendingRequestsViewOpen && (
            <Button
              variant="ghost"
              disabled={!hasUnreadNotifications}
              onClick={() => markAsRead()}
            >
              {t`clearAll`}
            </Button>
          )}
        </Header>

        {isPendingRequestsViewOpen ? (
          <PendingRequestsContent />
        ) : (
          <NotificationsContent
            notifications={notifications}
            isLoading={notificationsIsLoading}
          />
        )}
      </Container>
    </SidebarWrapper>
  );
};

const SidebarWrapper = styled(FixedPanelWithShadowLayer)`
  right: 0;
  background-color: ${({ theme }) => theme.colors.basics.white};

  ${({ theme }) =>
    theme.animations.slideInHorizontally('right', '800px', '0.3s')};

  @media (min-width: 1600px) {
    min-width: 686px;
    max-width: 1020px;
  }
`;

const Container = styled(Flexbox)`
  box-shadow: ${({ theme }) => theme.shadow.actionsPanel};
  height: 100vh;
  overflow: auto;
  background-color: ${colors.basics.canvas};
`;

const Header = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colors.basics.white};
  padding: 0 25px;
`;

const TitleContainer = styled(Flexbox)`
  ${({ theme }) => theme.typography.actionsPanel.acrossPanel.inPlatformLocation}
  ${({ theme }) => colorIcon(theme.colors.basics.black)}
  cursor: pointer;
  height: 75px;
`;

const TitleValue = styled('div', forwardRef)`
  ${({ theme }) => theme.mixins.ellipsis}
  max-width: 420px;
`;
